import React, { useState, useEffect, useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage } from '../../../../../util/reactIntl';

import { OutsideClickHandler } from '../../../../../components';

import css from './FilterCategories.module.css';

const translations = {
  en: [
    { id: 'culture', name: 'Culture' },
    { id: 'gastro', name: 'Gastro' },
    { id: 'retreat_holism', name: 'Retreat & holism' },
    { id: 'sportadrenalin', name: 'Sport & adrenaline' },
    { id: 'fishing', name: 'Fishing' },
    { id: 'luxury', name: 'Luxury experiences' },
    { id: 'accommodation', name: 'Experience & accommodation' },
    { id: 'enology', name: 'Enology' },
    { id: 'Medicaltourisim', name: 'Medical tourism' },
    { id: 'Museums_Galleries_id', name: 'Museums and Galleries' },
    { id: 'Free_tours_id', name: 'Free tours' },
  ],
  hr: [
    { id: 'culture', name: 'Kultura' },
    { id: 'gastro', name: 'Gastro' },
    { id: 'retreat_holism', name: 'Retreat & holizam' },
    { id: 'sportadrenalin', name: 'Sport & adrenalin' },
    { id: 'fishing', name: 'Ribolov' },
    { id: 'luxury', name: 'Luksuzna iskustva' },
    { id: 'accommodation', name: 'Experience & smještaj' },
    { id: 'enology', name: 'Enologija' },
    { id: 'Medicaltourisim', name: 'Medicinski turizam' },
    { id: 'Museums_Galleries_id', name: 'Muzeji i Galerije' },
    { id: 'Free_tours_id', name: 'Besplatne ture' },
  ],
  // Add other languages here...
};

const ALL_CATEGORIES_ID = 'all-categories';

const CategoryDropdown = ({
  input,
  className,
  rootClassName,
  categories = [],
  allCategoriesLabel,
  alignLeft,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setActiveIndex(-1);
    }
  };

  const handleOptionClick = (optionId) => {
    const value = optionId === ALL_CATEGORIES_ID ? '' : optionId;
    input.onChange(value);
    setHasSelected(true);
    setIsOpen(false);
  };

  const allOptions = [
    {
      id: ALL_CATEGORIES_ID,
      name: allCategoriesLabel || 'All categories',
    },
    ...categories,
  ];

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < allOptions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = allOptions[activeIndex];
      if (selected) handleOptionClick(selected.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const selectedCategory = categories.find(
    (category) => category.id === input.value
  );

  const labelText = selectedCategory
    ? selectedCategory.name
    : hasSelected && input.value === ''
    ? allCategoriesLabel
    : <FormattedMessage id="PageBuilder.SearchCTA.CategoryFilter.placeholder" />;

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  const optionRefs = useRef([]);

  return (
    <OutsideClickHandler className={classes} onOutsideClick={() => setIsOpen(false)}>
      <div className={css.dropdownContainer}>
        <div
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-owns="category-listbox"
          aria-controls="category-listbox"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={classNames(css.toggleButton, {
            [css.unselected]: !hasSelected && !selectedCategory && !isOpen,
            [css.placeholderOpened]: isOpen && !hasSelected,
          })}
        >
          <span className={css.dropdownItem}>{labelText}</span>
          <span className={classNames(css.chevron, isOpen && css.isOpen)} />
        </div>

        {isOpen && (
          <ul
            className={classNames(css.dropdownContent, {
              [css.alignLeft]: alignLeft,
            })}
            role="listbox"
            id="category-listbox"
          >
            {allOptions.map(({ id, name }, index) => {
              const isSelected =
                id === input.value || (id === ALL_CATEGORIES_ID && input.value === '');
              const isActive = index === activeIndex;
              return (
                <li
                  key={id}
                  ref={(option) => (optionRefs.current[index] = option)}
                  className={classNames(css.option, {
                    [css.activeOption]: isActive,
                  })}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleOptionClick(id)}
                >
                  <span
                    className={
                      isSelected
                        ? css.dropdownItemBorderSelected
                        : css.dropdownItemBorder
                    }
                  />
                  {name}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

const FilterCategories = (props) => {
  const { className, rootClassName, alignLeft, ...rest } = props;
  const [currentLang, setCurrentLang] = useState('en');

  useEffect(() => {
    const locale = localStorage.getItem('locale');
    if (locale && translations[locale]) {
      setCurrentLang(locale);
    }
  }, []);

  // Pass translated categories and translate "All categories" label as well here
  const categories = translations[currentLang];
  const allCategoriesLabel =
    currentLang === 'hr' ? 'Sve kategorije' : 'All categories';

  return (
    <Field
      {...rest}
      name="pub_categoryLevel1"
      component={CategoryDropdown}
      className={className}
      rootClassName={rootClassName}
      categories={categories}
      allCategoriesLabel={allCategoriesLabel}
      alignLeft={alignLeft}
    />
  );
};

export default FilterCategories;
